/** @format */

import React from 'react';
import Modal from '../shared/modal';
import { PrimaryButton, SecondaryButton, TextArea } from '../shared';
import { LogoutLoader } from '../shared/loaders';
import { AIImageGeneratorModalProps } from './types';
import { useAIImageGen } from './useAIImageGen';

const MODELS = [
  { value: 'gpt-image-1', label: 'gpt-image-1 (best quality)' },
  { value: 'gpt-image-1-mini', label: 'gpt-image-1-mini (faster / cheaper)' },
];

const SIZES = [
  { value: '1024x1024', label: 'Square (1024×1024)' },
  { value: '1536x1024', label: 'Landscape / wide (1536×1024)' },
  { value: '1024x1536', label: 'Portrait / tall (1024×1536)' },
];

const QUALITIES = [
  { value: 'low', label: 'Low (cheapest)' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High (best, pricier)' },
];

/** Banners look best wide; everything else (logos, food, categories) is square. */
const defaultSizeFor = (entityType: string): string =>
  entityType === 'BANNER' || entityType === 'BRAND_BANNER' ? '1536x1024' : '1024x1024';

export const AIImageGeneratorModal: React.FC<AIImageGeneratorModalProps> = ({
  active,
  setActive,
  entityType,
  entityId,
  defaultSubject = '',
  entityLabel,
  onAccept,
}) => {
  const displayLabel = entityLabel ?? entityType.replace(/_/g, ' ').toLowerCase();
  const [subject, setSubject] = React.useState(defaultSubject);
  const [model, setModel] = React.useState(MODELS[0].value);
  const [size, setSize] = React.useState(defaultSizeFor(entityType));
  const [quality, setQuality] = React.useState('medium');
  const { createJob, isGenerating, images, failed, errorMessage, reset } =
    useAIImageGen();

  // Reset transient state whenever the modal is (re)opened.
  React.useEffect(() => {
    if (active) {
      setSubject(defaultSubject);
      setSize(defaultSizeFor(entityType));
      setQuality('medium');
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleGenerate = () => {
    if (!subject.trim()) return;
    createJob({ entityType, entityId, subject: subject.trim(), model, size, quality });
  };

  const handleAccept = (url: string) => {
    onAccept(url);
    setActive(false);
  };

  return (
    <Modal active={active} onClick={setActive} close size="max-w-2xl">
      <div className="p-6">
        <h3 className="text-lg medium text-text">Generate image with AI</h3>
        <p className="text-sm text-text-placeholder mt-1">
          Describe the {displayLabel} you want. ChowCentral house styling and
          brand styling are applied automatically.
        </p>

        <div className="mt-5">
          <TextArea
            label="What should the image show?"
            name="subject"
            placeholder="e.g. a steaming plate of jollof rice with grilled chicken"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>

        <div className="mt-4">
          <label className="text-sm text-text medium" htmlFor="ai-image-model">
            Model
          </label>
          <select
            id="ai-image-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="mt-1 bg-subtle rounded-lg px-3 py-3.5 text-sm text-text w-full focus:outline-none focus:bg-white focus:border-primary border"
          >
            {MODELS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-text medium" htmlFor="ai-image-size">
              Size
            </label>
            <select
              id="ai-image-size"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className="mt-1 bg-subtle rounded-lg px-3 py-3.5 text-sm text-text w-full focus:outline-none focus:bg-white focus:border-primary border"
            >
              {SIZES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-text medium" htmlFor="ai-image-quality">
              Quality
            </label>
            <select
              id="ai-image-quality"
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
              className="mt-1 bg-subtle rounded-lg px-3 py-3.5 text-sm text-text w-full focus:outline-none focus:bg-white focus:border-primary border"
            >
              {QUALITIES.map((q) => (
                <option key={q.value} value={q.value}>
                  {q.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Result area */}
        {isGenerating && (
          <div className="mt-6 flex flex-col items-center justify-center gap-3 py-8 border border-dashed border-slate-medium rounded-xl bg-subtle">
            <LogoutLoader />
            <p className="text-sm text-text-placeholder">
              Generating… this can take up to a minute.
            </p>
          </div>
        )}

        {failed && !isGenerating && (
          <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            {errorMessage || 'Image generation failed. Please try again.'}
          </div>
        )}

        {!isGenerating && images.length > 0 && (
          <div className="mt-6">
            <p className="text-sm text-text bold mb-2">
              👆 Click an image to use it
            </p>
            <div className="grid grid-cols-2 gap-3">
              {images.map((url) => (
                <button
                  key={url}
                  type="button"
                  onClick={() => handleAccept(url)}
                  className="group relative rounded-xl overflow-hidden border border-mild hover:border-primary transition-colors"
                >
                  <img
                    src={url}
                    alt="AI generated"
                    className="w-full h-40 object-cover bg-gray-100"
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-primary text-white text-xs py-1.5 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                    Use this image
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          {/* type="button" is required: this modal renders inside entity <form>s,
              so without it these default to type="submit" and would submit the
              parent form (e.g. save the brand) on click. */}
          <SecondaryButton type="button" text="Close" onClick={() => setActive(false)} />
          <PrimaryButton
            type="button"
            text={images.length > 0 ? 'Regenerate' : 'Generate'}
            onClick={handleGenerate}
            loading={isGenerating}
            disabled={isGenerating || !subject.trim()}
          />
        </div>
      </div>
    </Modal>
  );
};
